import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Icon } from './ui/index.jsx';

/**
 * FloatingFeedbackButton
 * Mobile-only floating button to encourage beta feedback
 * Dismissible and saves state to localStorage
 * Uses Portal to escape stacking contexts and ensure stacking on top
 */
export default function FloatingFeedbackButton() {
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(true);
    const [isAnimating, setIsAnimating] = useState(false);

    // Check localStorage on mount
    useEffect(() => {
        const dismissed = localStorage.getItem('feedback_button_dismissed');
        if (dismissed === 'true') {
            setIsVisible(false);
        }
    }, []);

    const handleDismiss = (e) => {
        e.stopPropagation();
        setIsAnimating(true);

        // Save to localStorage
        localStorage.setItem('feedback_button_dismissed', 'true');

        // Wait for animation then hide
        setTimeout(() => {
            setIsVisible(false);
        }, 300);
    };

    const handleClick = () => {
        navigate('/contact');
    };

    if (!isVisible) return null;

    // Use Portal to render directly in body, avoiding stacking context traps
    return createPortal(
        <div
            className={`
                fixed bottom-6 right-6 z-[9999]
                flex items-center gap-2
                bg-[#0A0A0A]/60 backdrop-blur-xl
                border border-white/10
                rounded-full
                shadow-[0_8px_32px_rgba(0,0,0,0.5)]
                transition-all duration-300
                hover:border-[#7B61FF]/50
                hover:shadow-[0_0_20px_rgba(123,97,255,0.2)]
                group
                ${isAnimating ? 'opacity-0 scale-75 translate-y-4' : 'opacity-100 scale-100 translate-y-0'}
            `}
        >
            <button
                onClick={handleClick}
                className="flex items-center gap-2 pl-5 pr-3 py-3 rounded-l-full transition-colors group-hover:bg-white/5"
                aria-label="Give feedback"
            >
                <div className="relative">
                    <Icon name="mail" size="sm" className="text-white group-hover:text-[#7B61FF] transition-colors" />
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#7B61FF] rounded-full animate-pulse" />
                </div>
                <span className="text-white/90 font-medium text-sm group-hover:text-white transition-colors">Feedback</span>
            </button>

            {/* Separator */}
            <div className="w-px h-6 bg-white/10" />

            {/* Dismiss button */}
            <button
                onClick={handleDismiss}
                className="pr-4 pl-3 py-3 rounded-r-full hover:bg-white/10 transition-colors group"
                aria-label="Dismiss"
            >
                <Icon name="x" size="xs" className="text-white/50 group-hover:text-white transition-colors" />
            </button>
        </div>,
        document.body
    );
}
